import nim from '@yxim/nim-web-sdk/dist/SDK/NIM_Web_NIM.js'
import { entries, first, groupBy, last, sortBy, sortedUniqBy, uniqBy } from 'lodash-es'
import { mergeSortedObjectList } from './tools'
import type { ServiceRecordForQuery } from '~/api'

const nimCtx = {
  instance: null as any,
}

function useNim({
  account,
  token,
  onConnect,
  onDisconnect,
  onWillReconnect,
  onError,
  onMsg,
  ...otherOptions
}: any = {}): any {
  if (!nimCtx.instance) {
    nimCtx.instance = nim.getInstance({
      debug: false,
      appKey: import.meta.env.VITE_APP_CHAT_APP_KEY,
      account,
      authType: 1,
      db: false,
      token,
      needReconnect: true,
      quickReconnect: true,
      onconnect: onConnect,
      ondisconnect: onDisconnect,
      onwillreconnect: onWillReconnect,
      onerror: onError,
      onmsg: onMsg,
      ...otherOptions,
    })
  }
  return nimCtx.instance
}

function clearNim() {
  if (nimCtx.instance)
    nimCtx.instance.destroy()
  nimCtx.instance = null
}

async function queryRemoteHistoryForRange(instance: any, to: string, range: [number, number], lastMsgId?: string, lastMsgTime?: number) {
  let result: Message[] = []
  if (lastMsgTime && lastMsgTime < range[1] && lastMsgTime > range[0])
    range = [range[0], lastMsgTime]
  while (true) {
    const msgs = await queryRemoteHistorySingle(instance, to, range, lastMsgId)
    if (msgs.length) {
      result = [...msgs, ...result]
      lastMsgId = msgs[0].idServer
      range = [range[0], msgs[0].time]
    }
    else {
      break
    }
  }
  return result
}

function queryRemoteHistorySingle(instance: any, to: string, range: [number, number], lastMsgId?: string): Promise<Message[]> {
  return new Promise((resolve) => {
    instance.getHistoryMsgs({
      scene: 'p2p',
      to,
      lastMsgId,
      beginTime: range[0],
      endTime: range[1],
      asc: true,
      done: (error?: Error, obj?: { msgs: Message[] }) => {
        if (import.meta.env.DEV && error)
          console.warn(error)
        if (error)
          resolve([])
        else
          resolve(obj?.msgs || [])
      },
    })
  })
}

async function queryHistory(records: ServiceRecordForQuery[], lastMsgId?: string, lastMsgTime?: number): Promise<Message[]> {
  const normalizedRecords = records.map((record) => {
    const { serviceTimeList, ...others } = record
    return {
      ...others,
      range: serviceTimeList[0],
    }
  })
  const seatGroups = entries(groupBy(normalizedRecords, 'to'))

  const result = await Promise.all(seatGroups.map(async ([seat, records]) => {
    const { range: { startTime } } = last(records)!
    const { range: { endTime } } = first(records)!
    const instance = useNim()
    return queryRemoteHistoryForRange(instance, seat, [startTime, endTime], lastMsgId, lastMsgTime)
  }))

  const mergedMsgs = mergeSortedObjectList(result, 'time')
  return sortedUniqBy(mergedMsgs, 'idClient')
}

function createLocalCustomMsg(type: CustomMessageType, content?: CustomContent['content']) {
  const time = Date.now()
  return {
    flow: 'in',
    isLocal: true,
    time,
    from: '',
    fromNick: '',
    idClient: `Client-${time}`,
    idServer: '',
    isHistoryable: false,
    status: 'success',
    to: '',
    type: 'custom',
    contentDecoded: {
      type,
      content,
    },
  } as Message
}

export { useNim, clearNim, queryHistory, createLocalCustomMsg }
